import {Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, inject} from '@angular/core';
import {ValidationErrors} from '@angular/forms';
import {GetId} from '../../../_utils/get-id';
import * as _ from 'lodash';
import {TranslateModule} from '@ngx-translate/core';

/**
 * Part of this component's job is to programmatically set the aria-invalid attribute of the input field when appropriate.
 * This component will attempt to:
 *  - Ensure it has an ID
 *  - Ensure the input has aria-describedby set to this components' ID
 *  - Update the aria-invalid and aria-required attributes of the input element when appropriate
 *
 * Doing that depends on three pieces of info:
 *  - Whether or not there is an error
 *  - Whether or not the form wants errors shown to the user
 *  - A reference to the element that needs to be updated
 *
 * Component needs to:
 *  - Check if it has an ID, if not: create a unique ID for itself.
 *  - Check if the input element has the aria-describedby attribute set.  If not, inject it into element reference
 *  - Monitor the _errors and _displayErrors input values to trigger setting the aria-invalid and aria-required attribute on the element reference
 */
@Component({
    selector: 'app-validation-errors',
    templateUrl: './validation-errors.component.html',
    styleUrls: ['./validation-errors.component.scss'],
    imports: [TranslateModule]
})
export class ValidationErrorsComponent extends GetId implements OnInit, OnChanges {

  /**
   * Assign the FormGroup's Control's errors object to this.
   */
  @Input('errors') _errors: ValidationErrors | ValidationErrors[];

  /**
   * Assign the formSubmitted variable to this.  Or whatever is being used to indicate the form should display validation errors.
   * If the form always displays them, then just set this to true.  Default is false.
   */
  @Input() displayErrors: boolean = false;

  /**
   * Assign a reference to the associated input to this.  It's needed so that this component can manipulate the aria attributes when appropriate.
   */
  @Input() element: HTMLElement;

  /**
   * Used to indicate which aria attributes are to be set when appropriate.  This is useful if the manipulated dom element isn't allowed certain aria attributes.
   * Eg: a fieldset containing checkboxes doesn't appear to be allowed aria-required
   */
  private _ariaConfig: {invalid?: boolean; required?: boolean} = {invalid: true, required: true};
  private rootComponentElement = inject(ElementRef);

  constructor() {
    super();
    const rootComponentElement = this.rootComponentElement;


    //When initialized, we will ensure there is a unique ID

    //Check if there's an existing ID.  If not, set a unique one using the base class: GetId
    if(!rootComponentElement.nativeElement.getAttribute('id')) {
      //No ID hard-coded.  Set one automatically
      rootComponentElement.nativeElement.setAttribute('id', this.getId());
    }

  }

  ngOnInit() {

    //When Angular signals that the component is setup, make sure the element reference refers to this element by its id in the aria-describedby attribute

    //Only do the below if this component is being used with an element Input()
    if(this.element) {
      //Set the aria-describedby attribute of the passed-in element
      if(!this.element.getAttribute('aria-describedby')) {
        //No value set.  Set it to our ID.
        this.element.setAttribute('aria-describedby', this.rootComponentElement.nativeElement.getAttribute('id'));
      }
    } else {
      //For AODA, this logic is needed.  The parent component needs to provide the Input element
      console.warn('No element provided');
    }
  }

  //Monitor when the input values (errors or displayErrors) change.
  ngOnChanges(changes: SimpleChanges): void {
    if(this.element) {

      //When the input changes, we will check if we need to set the aria-invalid and/or aria-required attributes

      //If the form wants to display errors
      if(this.displayErrors) {
        //Set aria-invalid to reflect if there are errors or not
        if(this._ariaConfig.invalid) {
          this.element.setAttribute('aria-invalid', `${!_.isEmpty(this.errors)}`);
        }

        //If the errors object happens to contain a 'required' error, set the aria-required attribute as well
        if(this._ariaConfig.required && (this.errors.required || this.errors.oneOf)) {
          this.element.setAttribute('aria-required', 'true');
        }
      } else {
        //If the form doesn't want to display errors, remove the attributes entirely
        this.element.removeAttribute('aria-invalid');
        this.element.removeAttribute('aria-required');
      }
    }
  }

  public get errors(): ValidationErrors {
    //Since _errors can be a single instance or an array of the ValidationErrors object, convert either scenario into a single merged instance of the object
    return Object.assign({}, ...[].concat(this._errors));
  }

  public get containsPasswordValidationErrors(): boolean {
    return this.errors.tooShort || this.errors.tooLong || this.errors.doesNotContainLcAlpha
      || this.errors.doesNotContainUcAlpha || this.errors.doesNotContainNum || this.errors.doesNotContainSpecial
       || this.errors.doesNotContainSequentialAlpha || this.errors.doesNotContainSequentialNumber || this.errors.doesNotContainsWhiteSpace || this.errors.containsSequence;
  }

  /** @see {@link _ariaConfig} */
  get ariaConfig(): {invalid?: boolean; required?: boolean} {
    return this._ariaConfig;
  }

  /** @see {@link _ariaConfig} */
  @Input()
  set ariaConfig(value: {invalid?: boolean; required?: boolean}) {
    this._ariaConfig = Object.assign(this._ariaConfig, value);
    console.debug(`ariaConfig: ${JSON.stringify(this._ariaConfig)}`);
  }

}
